/**
 * DEV-ONLY workflow-trace fixtures — opt-in via localStorage.
 *
 * OFF BY DEFAULT, same pattern as every other dev seed in this app
 * (`devSeedHitl.js`, `devSeedTasks.js`): the honest (empty) state is what you
 * get until you explicitly ask for the mock.
 *
 *   On  :  localStorage.setItem('wq_enrich_trace', '1')   // then reload
 *   Off :  localStorage.removeItem('wq_enrich_trace')
 *
 * `import.meta.env.DEV` still gates it, so this can never reach a deployed
 * build. That is the line that matters: the defect this report flags elsewhere
 * (`MOCK_AUDIT_LEDGER`) is mock data with NO dev guard, visible in any
 * environment — not mock data that needs no flag in dev.
 *
 * WHY THIS EXISTS
 * No endpoint returns a workflow trace. Verified live against the tenant: the 5
 * HITL tasks carry `render` keys `{title, summary, ui_block, work_queue_event_type,
 * …type block}` and **nothing** matching trace/workflow/step; and no governance
 * endpoint (change-request, proposal, claim, promotion) has a source-workflow
 * concept at all. So `TraceSlideOut` is unreachable with real data — this seed
 * makes it demonstrable without faking anything in the live path.
 *
 * WHAT THE TRACE IS (and is not)
 * Not a history: it carries FUTURE steps too (`pending`, no timestamp — "Will
 * write approved claims…"). It is the pipeline's position: what ran, where it is
 * paused, what remains. That is a different thing from the audit trail (CT-5145),
 * which is past rows only. Which means it cannot come from an audit endpoint — it
 * has to come from whatever runs the graph.
 *
 * WHO COULD ACTUALLY PROVIDE IT
 *  - HITL types: realistic today. LangGraph owns the graph and the paused node,
 *    and `hitl/list` already returns `node_id` / `thread_id` / `hitl_type:
 *    "langgraph"`. The ask is only that `render` carry `trace[]`.
 *  - Governance types (Gov Promotion, Gov Review): a change request is created by
 *    a person or an agent, not produced by a pipeline. The prototype shows traces
 *    there because it draws them as INGESTION pipelines (document in → claims
 *    extracted → conflicts → human gate), which the backend does not model
 *    anywhere. That is a product question before a backend one: if those
 *    pipelines are not real, GOV_PROMOTION and GOV_REVIEW come out of
 *    TRACE_EVENT_TYPES and the list drops from 6 types to 4.
 *
 * PROPOSED CONTRACT (this is the shape to put in front of backend)
 * `render.trace: Array<{ id, label, state, description?, timestamp? }>` plus
 * `render.workflow` (display name) and `render.trace_ref` (workflow id).
 *   `state`: 'done' | 'paused' | 'error' | 'blocked' | 'pending'
 * The prototype names the same fields `{step, label, status, detail, timestamp}` —
 * kept in our vocabulary here because that is what `WorkflowTraceList` reads and
 * what `mapHitlToEvent` already passes straight through from `render.trace`.
 *
 * Content below is the prototype's own data, scraped from its bundle so the demo
 * matches what product signed off on. Delete with the rest of the dev scaffolding
 * (CT-5159).
 */

import { EVENT_TYPE } from './eventTypes.js';

export const TRACE_FIXTURE_FLAG = 'wq_enrich_trace';

const step = (id, label, state, description, timestamp) => ({
  id,
  label,
  state,
  ...(description ? { description } : {}),
  ...(timestamp ? { timestamp } : {}),
});

/** Prototype EVT-001 — the only trace captured from the live drawer, 5 steps. */
const DIAN_INTAKE = {
  workflow: 'DIAN Claim Extraction Pipeline',
  traceRef: 'WF-9901',
  trace: [
    step('s1', 'Document Received', 'done', 'Financial Policy PDF uploaded to /diane-intake/wealth-mgmt', '2026-06-17T08:00:00Z'),
    step('s2', 'Claim Extraction', 'done', '7 claims extracted by DIAN parser v2.4', '2026-06-17T08:01:30Z'),
    step('s3', 'Conflict Detection', 'done', '2 conflicts: GE-FIN-001 vs GE-FIN-007', '2026-06-17T08:02:00Z'),
    step('s4', 'Human Approval Gate', 'paused', 'Awaiting human approval before committing to Truth Registry', '2026-06-17T08:02:10Z'),
    step('s5', 'Commit to Truth', 'pending', 'Will write approved claims to Truth Registry'),
  ],
};

/** Prototype EVT-002. */
const EXTERNAL_COMMS = {
  workflow: 'SalesForecastPA External Comms',
  traceRef: 'WF-7712',
  trace: [
    step('s1', 'Forecast Generated', 'done', 'Q3 sales forecast compiled from CRM + pipeline data', '2026-06-17T09:10:00Z'),
    step('s2', 'Recipient Lookup', 'done', 'Recipient resolved: partner@gefinancial.com (external)', '2026-06-17T09:11:20Z'),
    step('s3', 'Confidence Check', 'done', 'Confidence 0.71 — below 0.85 threshold for auto-send', '2026-06-17T09:11:25Z'),
    step('s4', 'HITL Gate', 'paused', 'Waiting for human authorization to send external email', '2026-06-17T09:11:26Z'),
  ],
};

/** Prototype EVT-007 — the only one that exercises the `error` state. */
const QUOTE_PIPELINE = {
  workflow: 'Cross-Studio Quote Pipeline',
  traceRef: 'WF-0800',
  trace: [
    step('s1', 'Salesforce Data Pull', 'done', 'Account data pulled via SF v47 connector', '2026-06-17T06:00:00Z'),
    step('s2', 'DIAN Parse', 'error', 'Schema mismatch — expected v45 field `account_code`, got `accountId`', '2026-06-17T06:01:00Z'),
    step('s3', 'Quote Assembly', 'blocked', 'Blocked by the schema mismatch above'),
  ],
};

/** Prototype EVT-021. */
const LEAD_QUALIFICATION = {
  workflow: 'AdvisorCopilot Lead Qualification',
  traceRef: 'WF-4410',
  trace: [
    step('s1', 'Lead Captured', 'done', 'Inbound inquiry captured via web form', '2026-07-02T07:00:00Z'),
    step('s2', 'Qualification Scoring', 'done', 'Lead scored 94/100 — high intent', '2026-07-02T07:05:00Z'),
    step('s3', 'Threshold Check', 'done', 'Score exceeds the 90-point auto-handoff threshold', '2026-07-02T07:06:00Z'),
    step('s4', 'HTL Handoff', 'paused', 'Ownership transferred — agent will not continue', '2026-07-02T07:06:05Z'),
  ],
};

/** Prototype EVT-C001. */
const WEBCHAT = {
  workflow: 'Enterprise Inbound Lead Qualification',
  traceRef: 'WF-WEBCHAT-001',
  trace: [
    step('s1', 'Visitor identified', 'done'),
    step('s2', 'Intent classified: pricing inquiry', 'done'),
    step('s3', 'Canon queried — no enterprise tier match', 'done'),
    step('s4', 'HITL Approval — awaiting human', 'paused'),
  ],
};

/** Prototype EVT-C003. */
const VCARD = {
  workflow: 'VCard Prospect Engagement',
  traceRef: 'WF-VCARD-001',
  trace: [
    step('s1', 'VCard activated by prospect', 'done'),
    step('s2', 'Product interest identified: Data Studio', 'done'),
    step('s3', 'Human request detected', 'done'),
    step('s4', 'HTL Handoff — ownership transferred', 'done'),
  ],
};

/**
 * One trace per type that can have one. Keyed by the PROTOTYPE display type, so a
 * `change_request` row is seeded as the Gov Review it renders as.
 *
 * Only the 6 types in `TRACE_EVENT_TYPES` appear here — Question, Train Me,
 * Break Glass and Gov Change Request never show Trace in the prototype.
 */
export const TRACE_FIXTURE_BY_TYPE = {
  [EVENT_TYPE.GOV_PROMOTION]: DIAN_INTAKE,
  [EVENT_TYPE.HTL_CONTINUATION]: EXTERNAL_COMMS,
  [EVENT_TYPE.GOV_REVIEW]: QUOTE_PIPELINE,
  [EVENT_TYPE.HTL_HANDOFF]: LEAD_QUALIFICATION,
  [EVENT_TYPE.CLIENT_CONTINUATION]: WEBCHAT,
  [EVENT_TYPE.CLIENT_HANDOFF]: VCARD,
};

/**
 * True only in a dev build with the explicit opt-in flag set — same contract
 * as `devSeedHitl.js`/`devSeedTasks.js`. A blocked storage accessor must not
 * silently turn mock data ON, so it fails closed to `false`.
 */
export function isTraceFixtureEnabled() {
  if (!import.meta.env.DEV) return false;
  try {
    return localStorage.getItem(TRACE_FIXTURE_FLAG) === '1';
  } catch {
    return false;
  }
}

/**
 * Seeds `workflow` / `traceRef` / `trace` on a detail that has none. A real
 * `render.trace` always wins — the day backend ships one, the flag stops
 * mattering for that type.
 *
 * @param {string} displayType prototype type (already bucketed)
 * @param {Record<string, unknown>} detail
 */
export function applyTraceFixture(displayType, detail = {}) {
  if (!isTraceFixtureEnabled()) return detail;
  const fixture = TRACE_FIXTURE_BY_TYPE[displayType];
  if (!fixture) return detail;
  // An explicitly-present array — even empty — is real data (e.g. "this pipeline
  // genuinely has no steps"), not the absence of an answer. Only a missing/non-array
  // `trace` means nothing real has arrived yet.
  if (Array.isArray(detail.trace)) return detail;
  return { ...detail, ...fixture };
}
